import React from "react";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Stack,
  Select,
  TextField,
  Typography,
  IconButton as MuiIconButton,
  MenuItem as MuiMenuItem,
} from "@mui/material";

import SaveIcon from "@mui/icons-material/Save";

import useCaptcha from "../../../hooks/super/useCaptcha";
import useAuth from "../../../hooks/useAuth";

import { CaptchaType, UserType, WafNodeType } from "../../../utils/constants";

import { CollapseAlert, SnackbarAlert } from "../../../components/pages/common/styled";
import PageHeader from "../../../components/pages/_new/super/common/PageHeader";
import {
  TOKENS,
  SectionCard,
} from "../../../components/pages/_new/super/design";

function SACaptchaGeneral() {
  const {
    captchaType4Engine,
    captchaType4Edge,
    getCaptchaType,
    updateCaptchaType,
    captchaExpireTime4Engine,
    getCaptchaExpireTime,
    updateCaptchaExpireTime,
    captchaVerifyUrl4Engine,
    captchaVerifyUrl4Edge,
    getCaptchaVerifyUrl,
    updateCaptchaVerifyUrl,
    errMsg,
    setErr,
  } = useCaptcha();
  const { isAuthenticated, adminUserType } = useAuth();
  const captchaExpireTime4EngineRef = React.useRef(null);
  const [captchaExpireTime4EngineValue, setCaptchaExpireTime4EngineValue] = React.useState("");
  const captchaVerifyUrl4EngineRef = React.useRef(null);
  const [captchaVerifyUrl4EngineValue, setCaptchaVerifyUrl4EngineValue] = React.useState("");

  const captchaVerifyUrl4EdgeRef = React.useRef(null);
  const [captchaVerifyUrl4EdgeValue, setCaptchaVerifyUrl4EdgeValue] = React.useState("");

  const changeCaptchaExpireTime4Engine = (e) => setCaptchaExpireTime4EngineValue(e.target.value);
  const changeCaptchaVerifyUrl4Engine = (e) => setCaptchaVerifyUrl4EngineValue(e.target.value);
  const changeCaptchaVerifyUrl4Edge = (e) => setCaptchaVerifyUrl4EdgeValue(e.target.value);

  const [message, setMessage] = React.useState();
  const [success, setSuccess] = React.useState();
  const [snackOpen, setSnackOpen] = React.useState(false);
  const handleSnackClose = () => setSnackOpen(false);

  const [type4Engine, setType4Engine] = React.useState();
  const [type4Edge, setType4Edge] = React.useState();

  React.useEffect(() => {
    if (isAuthenticated) {
      getCaptchaType(WafNodeType.WAF_ENGINE);
      getCaptchaExpireTime(WafNodeType.WAF_ENGINE);
      getCaptchaVerifyUrl(WafNodeType.WAF_ENGINE);

      getCaptchaType(WafNodeType.RL_ENGINE);
      getCaptchaExpireTime(WafNodeType.RL_ENGINE);
      getCaptchaVerifyUrl(WafNodeType.RL_ENGINE);
    }
    return () => setErr(null);
  }, [isAuthenticated, getCaptchaType, getCaptchaExpireTime, getCaptchaVerifyUrl, setErr]);

  React.useEffect(() => {
    if (null === captchaType4Engine || undefined === captchaType4Engine) return;
    setType4Engine(captchaType4Engine);
  }, [captchaType4Engine]);
  React.useEffect(() => {
    if (null === captchaType4Edge || undefined === captchaType4Edge) return;
    setType4Edge(captchaType4Edge);
  }, [captchaType4Edge]);
  React.useEffect(() => {
    if (null === captchaExpireTime4Engine || undefined === captchaExpireTime4Engine) return;
    setCaptchaExpireTime4EngineValue(captchaExpireTime4Engine);
  }, [captchaExpireTime4Engine]);
  React.useEffect(() => {
    if (null === captchaVerifyUrl4Engine || undefined === captchaVerifyUrl4Engine) return;
    setCaptchaVerifyUrl4EngineValue(captchaVerifyUrl4Engine);
  }, [captchaVerifyUrl4Engine]);
  React.useEffect(() => {
    if (null === captchaVerifyUrl4Edge || undefined === captchaVerifyUrl4Edge) return;
    setCaptchaVerifyUrl4EdgeValue(captchaVerifyUrl4Edge);
  }, [captchaVerifyUrl4Edge]);

  const selectCaptchaType4Engine = (e) => setType4Engine(e.target.value);
  const selectCaptchaType4Edge = (e) => setType4Edge(e.target.value);

  const [errOpen, setErrOpen] = React.useState(false);
  React.useEffect(() => {
    if (errMsg) setErrOpen(true);
  }, [errMsg]);

  const saveCaptchaType4Engine = async () => {
    const result = await updateCaptchaType(WafNodeType.WAF_ENGINE, type4Engine);
    setMessage(result.msg);
    setSuccess(result.status);
    setSnackOpen(true);
  };
  const saveCaptchaType4Edge = async () => {
    const result = await updateCaptchaType(WafNodeType.RL_ENGINE, type4Edge);
    setMessage(result.msg);
    setSuccess(result.status);
    setSnackOpen(true);
  };
  const saveCaptchaExpireTime4Engine = async () => {
    const result = await updateCaptchaExpireTime(WafNodeType.WAF_ENGINE, captchaExpireTime4EngineValue);
    setMessage(result.msg);
    setSuccess(result.status);
    setSnackOpen(true);
  };
  const saveCaptchaVerifyUrl4Engine = async () => {
    const result = await updateCaptchaVerifyUrl(WafNodeType.WAF_ENGINE, captchaVerifyUrl4EngineValue);
    setMessage(result.msg);
    setSuccess(result.status);
    setSnackOpen(true);
  };
  const saveCaptchaVerifyUrl4Edge = async () => {
    const result = await updateCaptchaVerifyUrl(WafNodeType.RL_ENGINE, captchaVerifyUrl4EdgeValue);
    setMessage(result.msg);
    setSuccess(result.status);
    setSnackOpen(true);
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: `${TOKENS.radiusXs}px`,
      background: TOKENS.bgSurface,
    },
  };
  const labelSx = { fontSize: 12.5, color: TOKENS.fg2, mb: 0.75, fontWeight: 500 };
  const canEdit = [UserType.SUPER_ADMIN, UserType.ADMIN].includes(adminUserType);

  const SaveButton = ({ onClick, disabled }) => (
    <MuiIconButton
      onClick={onClick}
      disabled={disabled}
      sx={{
        width: 40,
        height: 40,
        borderRadius: `${TOKENS.radiusXs}px`,
        border: `1px solid ${TOKENS.primary}`,
        color: TOKENS.primary,
        "&:hover": { background: TOKENS.primaryTint },
        "&:disabled": { borderColor: TOKENS.border, color: TOKENS.fg3 },
      }}
    >
      <SaveIcon style={{ width: 18, height: 18 }} />
    </MuiIconButton>
  );

  return (
    <React.Fragment>
      <Helmet title="SA General Configuration" />
      <PageHeader title="Captcha General Settings" description="Configure captcha behaviour for WAF engines and edges" />
      <Box sx={{ mb: 3 }}>
        <CollapseAlert errOpen={errOpen} setErrOpen={setErrOpen} setErr={setErr} errMsg={errMsg} />
      </Box>

      <Stack spacing={2}>
        <SectionCard
          title="Captcha for WAF Engine"
          description="Used by the inline pingora WAF when challenging suspicious traffic"
        >
          <Stack spacing={2.5} sx={{ maxWidth: 600 }}>
            <Box>
              <Typography sx={labelSx}>Active captcha type</Typography>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Select
                  value={type4Engine !== undefined && type4Engine}
                  onChange={selectCaptchaType4Engine}
                  fullWidth
                  size="small"
                  sx={{
                    borderRadius: `${TOKENS.radiusXs}px`,
                    "& .MuiOutlinedInput-input": { fontSize: 14 },
                  }}
                >
                  <MuiMenuItem value={CaptchaType.HCAPTCHA}>hCaptcha</MuiMenuItem>
                  <MuiMenuItem value={CaptchaType.RECAPTCHA_V2_CHECKBOX}>reCaptchaV2 Checkbox</MuiMenuItem>
                  <MuiMenuItem value={CaptchaType.RECAPTCHA_V2_INVISIBLE}>reCaptchaV2 Invisible</MuiMenuItem>
                  <MuiMenuItem value={CaptchaType.RECAPTCHA_V3}>reCaptchaV3</MuiMenuItem>
                </Select>
                <SaveButton onClick={saveCaptchaType4Engine} disabled={!canEdit} />
              </Stack>
            </Box>
            <Box>
              <Typography sx={labelSx}>Captcha expire time (seconds)</Typography>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <TextField
                  fullWidth
                  size="small"
                  placeholder="e.g. 600"
                  value={captchaExpireTime4EngineValue}
                  onChange={changeCaptchaExpireTime4Engine}
                  inputRef={captchaExpireTime4EngineRef}
                  sx={inputSx}
                />
                <SaveButton onClick={saveCaptchaExpireTime4Engine} disabled={!canEdit} />
              </Stack>
            </Box>
            <Box>
              <Typography sx={labelSx}>Captcha verify URL</Typography>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <TextField
                  fullWidth
                  size="small"
                  placeholder="https://..."
                  value={captchaVerifyUrl4EngineValue}
                  onChange={changeCaptchaVerifyUrl4Engine}
                  inputRef={captchaVerifyUrl4EngineRef}
                  sx={inputSx}
                />
                <SaveButton onClick={saveCaptchaVerifyUrl4Engine} disabled={!canEdit} />
              </Stack>
            </Box>
          </Stack>
        </SectionCard>

        <SectionCard
          title="Captcha for WAF Edge"
          description="Used by the edge rate-limit engine for slow / suspect clients"
        >
          <Stack spacing={2.5} sx={{ maxWidth: 600 }}>
            <Box>
              <Typography sx={labelSx}>Active captcha type</Typography>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Select
                  value={type4Edge !== undefined && type4Edge}
                  onChange={selectCaptchaType4Edge}
                  fullWidth
                  size="small"
                  sx={{
                    borderRadius: `${TOKENS.radiusXs}px`,
                    "& .MuiOutlinedInput-input": { fontSize: 14 },
                  }}
                >
                  <MuiMenuItem value={CaptchaType.HCAPTCHA}>hCaptcha</MuiMenuItem>
                  <MuiMenuItem value={CaptchaType.RECAPTCHA_V2_CHECKBOX}>reCaptchaV2 Checkbox</MuiMenuItem>
                  <MuiMenuItem value={CaptchaType.RECAPTCHA_V2_INVISIBLE}>reCaptchaV2 Invisible</MuiMenuItem>
                  <MuiMenuItem value={CaptchaType.RECAPTCHA_V3}>reCaptchaV3</MuiMenuItem>
                </Select>
                <SaveButton onClick={saveCaptchaType4Edge} disabled={!canEdit} />
              </Stack>
            </Box>
            <Box>
              <Typography sx={labelSx}>Captcha verify URL</Typography>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <TextField
                  fullWidth
                  size="small"
                  placeholder="https://..."
                  value={captchaVerifyUrl4EdgeValue}
                  onChange={changeCaptchaVerifyUrl4Edge}
                  inputRef={captchaVerifyUrl4EdgeRef}
                  sx={inputSx}
                />
                <SaveButton onClick={saveCaptchaVerifyUrl4Edge} disabled={!canEdit} />
              </Stack>
            </Box>
          </Stack>
        </SectionCard>
      </Stack>
      <SnackbarAlert open={snackOpen} onClose={handleSnackClose} severity={success} message={message} />
    </React.Fragment>
  );
}
export default SACaptchaGeneral;
